import { App } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { ScreenOrientation } from '../../services/api.types'
import { DynamicType } from '../../services/dynamicType'
import { applyScreenOrientation } from './screenOrientation'

export const ORIENTATION_KEY = 'yumA11yOrientation'
const LEGACY_ZOOM_KEY = 'yumA11yZoom'

export async function getStoredOrientation(): Promise<ScreenOrientation> {
  try {
    const { value } = await Preferences.get({ key: ORIENTATION_KEY })
    return value === ScreenOrientation.PORTRAIT ? ScreenOrientation.PORTRAIT : ScreenOrientation.AUTO
  } catch {
    return ScreenOrientation.AUTO
  }
}

export async function setStoredOrientation(value: ScreenOrientation): Promise<void> {
  await Preferences.set({ key: ORIENTATION_KEY, value })
  await applyScreenOrientation(value)
}

// Event name used by `useOsScale` to subscribe to changes.
export const OS_SCALE_EVENT = 'yum:os-scale-change'

function applyOsScale(scale: number): void {
  if (typeof document === 'undefined' || !document.documentElement) return
  document.documentElement.style.setProperty('--os-scale', String(scale))
  // Body class so CSS can branch on enlarged-text without needing container
  // style queries. Used by YumHeader (and possibly others) to stack on mobile
  // when the user has bumped OS text size.
  if (document.body) {
    document.body.classList.toggle('os-scale-large', scale > 1)
  }
  // Notify JS-side consumers (e.g. Victory charts that need to re-render with
  // updated font sizes — CSS rem doesn't apply inside SVG <text fontSize=…>).
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<number>(OS_SCALE_EVENT, { detail: scale }))
  }
}

export function getOsScale(): number {
  if (typeof document === 'undefined' || !document.documentElement) return 1
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--os-scale').trim()
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : 1
}

async function readAndApplyScale(): Promise<void> {
  const { scale } = await DynamicType.getScale()
  applyOsScale(scale)
}

async function initOsScale(): Promise<void> {
  // eslint-disable-next-line no-console
  console.log('[a11y] initOsScale platform=', Capacitor.getPlatform(), 'native=', Capacitor.isNativePlatform())
  if (!Capacitor.isNativePlatform()) return
  try {
    const result = await DynamicType.getScale()
    // eslint-disable-next-line no-console
    console.log('[a11y] DynamicType.getScale ->', result)
    applyOsScale(result.scale)
    await DynamicType.addListener('change', ({ scale }) => {
      // eslint-disable-next-line no-console
      console.log('[a11y] DynamicType change ->', scale)
      applyOsScale(scale)
    })
    // iOS posts the contentSizeCategory change while the app is foregrounded,
    // but if the user adjusts Text Size in Settings while the app is in the
    // background, we re-read on resume to catch up.
    App.addListener('appStateChange', (state) => {
      if (state.isActive) {
        readAndApplyScale().catch(() => undefined)
      }
    })
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[a11y] DynamicType plugin unavailable:', err)
  }
}

export async function applyA11yPrefs(): Promise<void> {
  // Best-effort cleanup of the legacy zoom preference from when the in-app
  // Zoom feature drove CSS zoom / viewport meta. OS Dynamic Type replaces it.
  Preferences.remove({ key: LEGACY_ZOOM_KEY }).catch(() => undefined)

  const orientation = await getStoredOrientation()
  await Promise.all([applyScreenOrientation(orientation), initOsScale()])
}
